import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  ArrowLeftIcon,
  StarIcon as StarOutline,
  ChatBubbleLeftRightIcon,
  ArrowUpOnSquareIcon,
  ChevronDownIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import {
  StarIcon,
  ClockIcon,
  FireIcon,
  PlayIcon,
} from "@heroicons/react/24/solid";
import { revealProps } from "../common/Reveal";
import cn from "classnames";
import AiDisclaimer from "./AiDisclaimer";
import Button from "../common/Button";
import Dropdown from "../Settings/Dropdown";
import Tooltip from "../common/Tooltip";
import SlideDrawer from "../common/SlideDrawer";
import {
  api,
  CallDetailData,
  DISMISS_REASONS,
  callParties,
  formatMs,
  LEAD_CATEGORY_LABELS,
  PIPELINE_LABELS,
  scoreClass,
  SEVERITY_ACTIVE_CLASSES,
  SEVERITY_CLASSES,
} from "./api";
import InvestigationChat from "./InvestigationChat";
import InvestigationTranscript, {
  TranscriptHandle,
} from "./InvestigationTranscript";
import ExportTranscriptModal from "./ExportTranscriptModal";
import TriageButton from "./TriageButton";
import SpeakerModal from "./SpeakerModal";
import { useNowPlaying } from "./NowPlayingContext";
import ResizeHandle from "../common/ResizeHandle";
import { useResizablePanel } from "../../hooks/useResizablePanel";

const REVIEW_OPTIONS: { value: string; label: string }[] = [
  { value: "unreviewed", label: "Unreviewed" },
  { value: "in_progress", label: "In progress" },
  { value: "reviewed", label: "Reviewed" },
  { value: "flagged", label: "Flagged" },
  { value: "irrelevant", label: "Irrelevant" },
];

const CallDetail: React.FC = () => {
  const { investigationId = "", callId = "" } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Back returns to wherever the detective came from (Mentions, Leads, a
  // chat citation…); only a cold load falls back to the Calls list.
  const goBack = () => {
    if (location.key !== "default") navigate(-1);
    else navigate(`/investigations/${investigationId}/calls`);
  };
  const [call, setCall] = useState<CallDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const summaryPanel = useResizablePanel({
    storageKey: "pnai-callSummaryWidth",
    defaultWidth: 384,
    minWidth: 260,
    maxWidth: 720,
    side: "left",
    maxViewportFraction: 0.5,
  });
  const [exportOpen, setExportOpen] = useState(false);
  // Case number for the export dialog's default "Case ID"; falls back to the
  // investigation id when none was entered.
  const [caseNumber, setCaseNumber] = useState<string | null>(null);
  useEffect(() => {
    api
      .get(investigationId)
      .then((d) => setCaseNumber(d.case_number || null))
      .catch(() => {});
  }, [investigationId]);
  const [notes, setNotes] = useState("");
  // Sidebar flag list: filter by triage state and category, sort by time or severity.
  const [flagStatus, setFlagStatus] = useState<
    "open" | "all" | "confirmed" | "dismissed"
  >("open");
  const [flagCategory, setFlagCategory] = useState("all");
  const [flagSort, setFlagSort] = useState<"time" | "severity">("time");
  const transcriptRef = useRef<TranscriptHandle>(null);
  const initialSeek = params.get("t") ? Number(params.get("t")) : undefined;
  const { audio, attachSource, currentMs } = useNowPlaying();

  const load = useCallback(async () => {
    try {
      const data = await api.call(investigationId, callId);
      // Video files carry a call row for their transcript; their home is the
      // video view on the file page.
      if ((data.file as any)?.kind === "video") {
        const t = params.get("t");
        navigate(`/investigations/${investigationId}/files/${data.file.id}${t ? `?t=${t}` : ""}`, { replace: true });
        return;
      }
      setCall(data);
      setNotes(data.analyst_notes || "");
    } catch {
      toast.error("Failed to load call");
    } finally {
      setLoading(false);
    }
  }, [investigationId, callId]);

  useEffect(() => {
    load();
  }, [load]);

  // Point the shared player at this call. If it's already playing this call
  // (arrived from the mini player) nothing restarts; a ?t= seeks.
  useEffect(() => {
    if (!call?.audioUrl || !call.transcript) return;
    attachSource(
      investigationId,
      callId,
      call.audioUrl,
      initialSeek != null ? { startMs: initialSeek, autoplay: false } : {},
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call?.audioUrl, callId]);

  // Poll while the pipeline is still running.
  useEffect(() => {
    if (!call || ["complete", "failed"].includes(call.pipeline_stage)) return;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [call, load]);

  const patch = async (body: Record<string, unknown>) => {
    if (!call) return;
    try {
      const updated = await api.updateCall(investigationId, callId, body);
      setCall({ ...call, ...updated });
    } catch {
      toast.error("Failed to save");
    }
  };

  // Same exit as the Leads tab: the card swipes off and fades, then its
  // row collapses, when a decision moves it out of the current filter.
  const [leaving, setLeaving] = useState<
    Record<
      string,
      { kind: "confirmed" | "dismissed"; phase: "swipe" | "collapse" }
    >
  >({});
  const setLeadStatus = async (
    leadId: string,
    status: string,
    dismiss_reason?: string,
  ) => {
    if (!call) return;
    const willLeave =
      status !== "new" && !(flagStatus === "all" || flagStatus === status);
    try {
      const updated = await api.updateLead(investigationId, leadId, {
        status,
        ...(dismiss_reason ? { dismiss_reason } : {}),
      });
      setCall({
        ...call,
        leads: call.leads.map((l) => (l.id === leadId ? updated : l)),
      });
      if (willLeave) {
        const kind = status as "confirmed" | "dismissed";
        setLeaving((prev) => ({ ...prev, [leadId]: { kind, phase: "swipe" } }));
        setTimeout(
          () =>
            setLeaving((prev) =>
              prev[leadId]
                ? { ...prev, [leadId]: { kind, phase: "collapse" } }
                : prev,
            ),
          380,
        );
        setTimeout(
          () =>
            setLeaving((prev) => {
              const n = { ...prev };
              delete n[leadId];
              return n;
            }),
          720,
        );
      }
    } catch {
      toast.error("Failed to update lead");
    }
  };

  const [speakerEdit, setSpeakerEdit] = useState<string | null>(null);
  // Right rail keeps its scroll position across history back, like the transcript.
  const sideRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!call) return;
    const saved = Number(
      sessionStorage.getItem(`inv.call.side.${callId}`) || 0,
    );
    if (saved > 0 && sideRef.current) sideRef.current.scrollTop = saved;
  }, [call, callId]);

  // Sticky jump nav for the rail. Only sections that actually render get an
  // entry, so the pills mirror what's below them.
  const railSections = useMemo(() => {
    if (!call) return [] as { id: string; label: string; count?: number }[];
    const list: { id: string; label: string; count?: number }[] = [];
    if (call.pipeline_stage === "complete")
      list.push({ id: "summary", label: "Summary" });
    list.push({
      id: "flags",
      label: "Flagged moments",
      count: call.leads.filter((l) => l.status !== "dismissed").length,
    });
    if (call.glossary && call.glossary.length > 0)
      list.push({
        id: "slang",
        label: "Slang & coded language",
        count: call.glossary.length,
      });
    if (call.mentions.length > 0)
      list.push({
        id: "people",
        label: "People & things mentioned",
        count: new Set(call.mentions.map((m) => m.entity.id)).size,
      });
    list.push({ id: "notes", label: "Notes" });
    if (call.topics && call.topics.length > 0)
      list.push({ id: "topics", label: "Topics", count: call.topics.length });
    return list;
  }, [call]);
  const [activeSection, setActiveSection] = useState("");
  const activeRailSection =
    railSections.find((sec) => sec.id === activeSection) || railSections[0];
  // Set while a click-initiated smooth scroll is in flight so the pills don't
  // flicker through every section on the way to the target.
  const jumpTimer = useRef<number | null>(null);
  const syncActiveSection = useCallback(() => {
    const el = sideRef.current;
    if (!el || jumpTimer.current != null) return;
    const sections = el.querySelectorAll<HTMLElement>("[data-rail-section]");
    if (sections.length === 0) return;
    const line = el.getBoundingClientRect().top + 40;
    let current = sections[0].dataset.railSection || "";
    sections.forEach((sec) => {
      if (sec.getBoundingClientRect().top <= line)
        current = sec.dataset.railSection || current;
    });
    // Short tails can never reach the top line; at the bottom, the last one wins.
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 2)
      current = sections[sections.length - 1].dataset.railSection || current;
    setActiveSection(current);
  }, []);
  useEffect(() => {
    syncActiveSection();
  }, [railSections, syncActiveSection]);
  useEffect(
    () => () => {
      if (jumpTimer.current != null) window.clearTimeout(jumpTimer.current);
    },
    [],
  );
  const jumpToSection = (id: string) => {
    const el = sideRef.current;
    const target = el?.querySelector<HTMLElement>(
      `[data-rail-section="${id}"]`,
    );
    if (!el || !target) return;
    setActiveSection(id);
    if (jumpTimer.current != null) window.clearTimeout(jumpTimer.current);
    jumpTimer.current = window.setTimeout(() => {
      jumpTimer.current = null;
      syncActiveSection();
    }, 700);
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const flagCounts = useMemo(() => {
    const leads = call?.leads || [];
    const byCategory: Record<string, number> = {};
    for (const l of leads)
      byCategory[l.category] = (byCategory[l.category] || 0) + 1;
    return {
      open: leads.filter((l) => l.status === "new").length,
      confirmed: leads.filter((l) => l.status === "confirmed").length,
      dismissed: leads.filter((l) => l.status === "dismissed").length,
      byCategory,
    };
  }, [call?.leads]);
  // Flags whose moment is playing right now. Uses the same segment-overlap
  // test as the transcript's red rows so both sides light up together.
  const activeFlagIds = useMemo(() => {
    const segs = call?.transcript?.segments || [];
    const seg = segs.find((x) => currentMs >= x.startMs && currentMs < x.endMs);
    if (!seg) return new Set<string>();
    const ids = new Set<string>();
    for (const l of call?.leads || []) {
      const startMs = l.start_ms || 0;
      const endMs = l.end_ms != null && l.end_ms > startMs ? l.end_ms : startMs;
      if (seg.startMs < endMs + 1 && seg.endMs > startMs) ids.add(l.id);
    }
    return ids;
  }, [call?.transcript?.segments, call?.leads, currentMs]);
  // When a flag becomes active (playback reaches it, or the detective clicked
  // its line in the transcript), bring its card into view in the rail.
  const activeKey = [...activeFlagIds].sort().join("|");
  useEffect(() => {
    if (!activeKey || !sideRef.current) return;
    const first = activeKey.split("|")[0];
    sideRef.current
      .querySelector<HTMLElement>(`[data-lead="${first}"]`)
      ?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [activeKey]);
  const visibleFlags = useMemo(() => {
    const leads = call?.leads || [];
    const list = leads.filter((l) => {
      if (leaving[l.id]) return true; // stays while it animates out
      if (flagStatus === "open" && l.status !== "new") return false;
      if (flagStatus === "confirmed" && l.status !== "confirmed") return false;
      if (flagStatus === "dismissed" && l.status !== "dismissed") return false;
      if (flagCategory !== "all" && l.category !== flagCategory) return false;
      return true;
    });
    return [...list].sort((a, b) =>
      flagSort === "severity"
        ? b.severity - a.severity || (a.start_ms || 0) - (b.start_ms || 0)
        : (a.start_ms || 0) - (b.start_ms || 0),
    );
  }, [call?.leads, flagStatus, flagCategory, flagSort, leaving]);
  const saveSpeaker = async (
    label: string,
    values: { name: string; role: string; propagate?: boolean },
  ) => {
    if (!call) return;
    try {
      const updated = await api.setSpeaker(investigationId, callId, {
        label,
        ...values,
      });
      // Suggestions and sibling counts change with the name; reload to be exact.
      await load();
      if (updated.propagated > 0)
        toast.success(
          `Named on this call and ${updated.propagated} other${updated.propagated === 1 ? "" : "s"} to this number`,
        );
    } catch {
      toast.error("Failed to update speaker");
      throw new Error("save failed");
    }
  };
  const dismissSuggestion = async (label: string, name: string) => {
    if (!call) return;
    try {
      await api.setSpeaker(investigationId, callId, { label, dismiss: name });
      await load();
    } catch {
      toast.error("Failed to update speaker");
    }
  };

  if (loading) {
    return (
      <div className="flex-1 overflow-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-base-300 rounded w-1/3" />
          <div className="h-64 bg-base-300 rounded" />
        </div>
      </div>
    );
  }
  if (!call) return <div className="flex-1 p-6">Call not found.</div>;

  const done = call.pipeline_stage === "complete";

  return (
    <div className="flex-1 flex min-h-0">
      <div className="flex-1 flex flex-col min-w-0">
        <div className="px-4 py-3 border-b border-base-300 flex items-center gap-3 flex-wrap">
          <Button variant="secondary" size="sm" onClick={goBack}>
            <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="font-semibold text-base-content truncate">
              {call.file.original_file_name}
            </h1>
            <p className="text-xs text-base-content/60">
              {call.call_started_at
                ? new Date(call.call_started_at).toLocaleString()
                : "Date unknown"}
              {` · ${callParties(call).primary}`}
              {callParties(call).secondary
                ? ` (${callParties(call).secondary})`
                : ""}
              {call.facility ? ` · ${call.facility}` : ""}
              {call.duration_ms ? ` · ${formatMs(call.duration_ms)}` : ""}
              {call.primary_language ? ` · ${call.primary_language}` : ""}
              {call.has_third_party ? " · third party present" : ""}
            </p>
          </div>
          <span
            className={cn(
              "px-2 py-0.5 rounded-full text-xs font-semibold",
              scoreClass(call.relevance_score),
            )}
          >
            {call.relevance_score != null
              ? `Relevance ${call.relevance_score}`
              : PIPELINE_LABELS[call.pipeline_stage]}
          </span>
          {/* Same star as the case editor's favorite toggle. */}
          <Tooltip
            placement="bottom"
            overlay={call.is_starred ? "Remove star" : "Star this call"}
          >
            <button
              type="button"
              onClick={() => patch({ is_starred: !call.is_starred })}
              className="p-1 leading-none transition-opacity hover:opacity-60"
            >
              {call.is_starred ? (
                <StarIcon className="size-5 text-yellow-500" />
              ) : (
                <StarOutline className="size-5 text-base-content/40" />
              )}
            </button>
          </Tooltip>
          <Dropdown
            size="sm"
            options={REVIEW_OPTIONS}
            value={call.review_status}
            onChange={(v) => patch({ review_status: v })}
          />
          {call.transcript && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setExportOpen(true)}
              title="Export transcript as PDF or TXT"
            >
              {/* Same as the case editor's Export button (EditorMenuBar). */}
              <div className="flex items-center">
                <ArrowUpOnSquareIcon className="size-6 mr-1 hidden xl:inline-block" />
                <p>Export</p>
              </div>
            </Button>
          )}
          <Button
            variant={chatOpen ? "primary" : "secondary"}
            size="sm"
            onClick={() => setChatOpen((o) => !o)}
          >
            <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" /> Ask
          </Button>
        </div>

        <div className="flex-1 flex min-h-0">
          <div className="flex-1 min-w-0 border-r border-base-300">
            {call.file.privileged_at ? (
              <div className="p-6 text-base-content/70">
                This file was marked privileged. Its transcript and AI analysis
                have been removed.
              </div>
            ) : call.transcript ? (
              <InvestigationTranscript
                ref={transcriptRef}
                transcript={call.transcript}
                audioUrl={call.audioUrl}
                participants={call.participants}
                flags={call.leads.filter((l) => l.status !== "dismissed")}
                onRenameSpeaker={setSpeakerEdit}
                suggestions={call.speaker_suggestions}
                onConfirmSuggestion={(s) =>
                  saveSpeaker(s.label, {
                    name: s.name,
                    role: "outside",
                    propagate: false,
                  })
                }
                onDismissSuggestion={(s) => dismissSuggestion(s.label, s.name)}
                initialSeekMs={initialSeek}
                scrollKey={`inv.call.scroll.${callId}`}
                audio={audio}
              />
            ) : (
              <div className="p-6 text-base-content/70">
                {call.pipeline_stage === "failed" ? (
                  <div className="text-red-600 dark:text-red-400">
                    {call.error_message ||
                      call.file.error_message ||
                      "Processing failed."}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="size-2 rounded-full bg-amber-500 animate-pulse" />{" "}
                    {PIPELINE_LABELS[call.pipeline_stage]}…
                  </div>
                )}
                <audio className="mt-4 w-full" controls src={call.audioUrl} />
              </div>
            )}
          </div>

          {/* The handle lives on a non-scrolling wrapper so it spans the full
              rail height regardless of how far the rail's content is scrolled. */}
          <div
            style={{ width: summaryPanel.width }}
            className="relative shrink-0 flex flex-col min-h-0 bg-base-200/40"
          >
            <ResizeHandle
              edge="left"
              active={summaryPanel.isResizing}
              {...summaryPanel.handleProps}
            />
            {railSections.length > 1 && (
              <div className="shrink-0 px-3 py-2 bg-base-100/85 backdrop-blur border-b border-base-300">
                {/* DaisyUI dropdown (same as the dismiss menu): opens on focus, closes when focus leaves.
                    The trigger reads as a section indicator that tracks scroll; opening it jumps. */}
                <div className="dropdown dropdown-bottom w-full">
                  <button
                    type="button"
                    tabIndex={0}
                    aria-label="Jump to section"
                    className="w-full flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-base-content hover:bg-base-200 transition-colors"
                  >
                    <span className="text-base-content/50 shrink-0">On</span>
                    <span className="truncate">{activeRailSection?.label}</span>
                    {activeRailSection?.count != null && (
                      <span className="tabular-nums text-base-content/50 shrink-0">
                        {activeRailSection.count}
                      </span>
                    )}
                    <ChevronDownIcon className="size-3.5 ml-auto shrink-0 text-base-content/50" />
                  </button>
                  <ul
                    tabIndex={0}
                    className="dropdown-content menu menu-sm bg-base-100 rounded-lg z-[30] w-full p-1 shadow border border-base-300 mt-1"
                  >
                    {railSections.map((sec) => {
                      const active = activeSection === sec.id;
                      return (
                        <li key={sec.id}>
                          <button
                            type="button"
                            onClick={() => {
                              jumpToSection(sec.id);
                              (
                                document.activeElement as HTMLElement | null
                              )?.blur();
                            }}
                            className={cn(
                              "flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-base-200",
                              active && "font-semibold",
                            )}
                          >
                            <span className="truncate">{sec.label}</span>
                            {sec.count != null && (
                              <span className="tabular-nums text-base-content/50">
                                {sec.count}
                              </span>
                            )}
                            {active && (
                              <CheckIcon className="size-3.5 ml-auto text-base-content/70" />
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            )}
            <aside
              ref={sideRef}
              onScroll={() => {
                if (!sideRef.current) return;
                sessionStorage.setItem(
                  `inv.call.side.${callId}`,
                  String(sideRef.current.scrollTop),
                );
                syncActiveSection();
              }}
              className="flex-1 min-w-0 overflow-auto p-4 space-y-5 text-sm scroll-pt-4"
            >
              {done && (
                <section data-rail-section="summary">
                  <h3 className="font-semibold text-base-content mb-1">
                    Summary
                  </h3>
                  <AiDisclaimer kind="callSummary" size="sm" className="mb-2" />
                  <p className="text-base-content/80">{call.summary}</p>
                  {call.relevance_reason && (
                    <p className="mt-1 text-xs text-base-content/60">
                      {call.relevance_reason}
                    </p>
                  )}
                </section>
              )}
              <section data-rail-section="flags">
                <h3 className="font-semibold text-base-content mb-1">
                  Flagged moments (
                  {call.leads.filter((l) => l.status !== "dismissed").length})
                </h3>
                {call.leads.length > 0 && (
                  <AiDisclaimer kind="flags" size="sm" className="mb-2" />
                )}
                {call.leads.length === 0 && (
                  <p className="text-base-content/60">
                    {done ? "Nothing flagged." : "Pending analysis."}
                  </p>
                )}
                {call.leads.length > 0 && (
                  <div className="mb-3 space-y-2">
                    {/* Decision: a segmented control, one glance shows where the review stands. */}
                    <div className="flex rounded-lg bg-base-200 p-0.5 text-[11px] font-medium">
                      {(
                        [
                          ["open", "Open", "Still to review", flagCounts.open],
                          ["confirmed", "✓", "Confirmed", flagCounts.confirmed],
                          ["dismissed", "✕", "Dismissed", flagCounts.dismissed],
                          ["all", "All", "Every flag", call.leads.length],
                        ] as const
                      ).map(([value, label, hint, n]) => (
                        <Tooltip key={value} placement="top" overlay={hint}>
                          <button
                            type="button"
                            onClick={() => setFlagStatus(value)}
                            className={cn(
                              "flex-1 min-w-0 px-1 py-1 rounded-md transition-colors whitespace-nowrap",
                              flagStatus === value
                                ? "bg-base-100 text-base-content shadow-sm"
                                : "text-base-content/60 hover:text-base-content",
                              value === "confirmed" &&
                                flagStatus === value &&
                                "text-green-700 dark:text-green-400",
                            )}
                          >
                            {label}{" "}
                            <span
                              className={cn(
                                "tabular-nums",
                                flagStatus === value
                                  ? "text-base-content/60"
                                  : "text-base-content/40",
                              )}
                            >
                              {n}
                            </span>
                          </button>
                        </Tooltip>
                      ))}
                    </div>
                    {/* Category + sort share one quiet line. */}
                    <div className="flex items-center gap-2 text-[11px]">
                      <Dropdown
                        size="xs"
                        className="!max-w-none min-w-0"
                        contentClassName="w-48"
                        options={[
                          { value: "all", label: `All categories (${call.leads.length})` },
                          ...Object.entries(flagCounts.byCategory).map(([cat, n]) => ({
                            value: cat,
                            label: `${LEAD_CATEGORY_LABELS[cat] || cat} (${n})`,
                          })),
                        ]}
                        value={flagCategory}
                        onChange={setFlagCategory}
                      />
                      <div
                        className="ml-auto inline-flex rounded-md border border-base-300 overflow-hidden shrink-0"
                        role="group"
                        aria-label="Sort"
                      >
                        <Tooltip placement="topRight" overlay="Sort by time">
                          <button
                            type="button"
                            onClick={() => setFlagSort("time")}
                            className={cn(
                              "px-1.5 py-1 transition-colors",
                              flagSort === "time"
                                ? "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300"
                                : "text-base-content/50 hover:bg-base-200",
                            )}
                          >
                            <ClockIcon className="size-3.5" />
                          </button>
                        </Tooltip>
                        <Tooltip
                          placement="topRight"
                          overlay="Sort by severity"
                        >
                          <button
                            type="button"
                            onClick={() => setFlagSort("severity")}
                            className={cn(
                              "px-1.5 py-1 border-l border-base-300 transition-colors",
                              flagSort === "severity"
                                ? "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300"
                                : "text-base-content/50 hover:bg-base-200",
                            )}
                          >
                            <FireIcon className="size-3.5" />
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                )}
                {call.leads.length > 0 && visibleFlags.length === 0 && (
                  <p className="text-xs text-base-content/60">
                    Nothing matches these filters.
                  </p>
                )}
                {/* Keyed on the filter/sort so a change remounts the list and replays the staggered reveal. */}
                <ul
                  key={`${flagStatus}|${flagCategory}|${flagSort}`}
                  className="-mb-2"
                >
                  {visibleFlags.map((l, i) => (
                    <li
                      key={l.id}
                      style={revealProps(i).style}
                      className={cn(
                        "reveal grid transition-[grid-template-rows] duration-300 ease-in-out",
                        leaving[l.id]?.phase === "collapse"
                          ? "grid-rows-[0fr]"
                          : "grid-rows-[1fr]",
                      )}
                    >
                      {/* overflow-hidden only while collapsing, so the dismiss menu isn't clipped. */}
                      <div
                        className={cn(
                          "min-h-0 pb-2",
                          leaving[l.id] && "overflow-hidden",
                        )}
                      >
                        <div
                          data-lead={l.id}
                          onClick={() =>
                            transcriptRef.current?.seek(l.start_ms || 0)
                          }
                          title="Jump to this moment"
                          className={cn(
                            "rounded-lg border p-2 cursor-pointer transition-[transform,opacity,background-color,border-color] duration-[380ms] ease-out hover:shadow-sm",
                            leaving[l.id] ? "opacity-0" : "opacity-100",
                            leaving[l.id]?.kind === "confirmed" &&
                              "translate-x-[60%] border-green-500 bg-green-50 dark:bg-green-950/40",
                            leaving[l.id]?.kind === "dismissed" &&
                              "-translate-x-[60%] border-base-300 bg-base-200",
                            !leaving[l.id] &&
                              (activeFlagIds.has(l.id)
                                ? cn(
                                    "shadow-sm",
                                    SEVERITY_ACTIVE_CLASSES[l.severity] ||
                                      SEVERITY_ACTIVE_CLASSES[3],
                                  )
                                : "border-base-300 bg-base-100 hover:border-red-300 dark:hover:border-red-800 hover:bg-red-50/60 dark:hover:bg-red-950/30"),
                            l.status === "dismissed" &&
                              !leaving[l.id] &&
                              "opacity-50",
                          )}
                        >
                          <div className="flex items-center gap-1 flex-wrap">
                            <span
                              className={cn(
                                "text-[11px] font-semibold px-1.5 rounded",
                                SEVERITY_CLASSES[l.severity],
                              )}
                            >
                              sev {l.severity}
                            </span>
                            <span className="text-xs font-medium">
                              {LEAD_CATEGORY_LABELS[l.category]}
                            </span>
                            <button
                              className="ml-auto text-xs text-blue-600 dark:text-blue-300 hover:underline"
                              onClick={(e) => {
                                e.stopPropagation();
                                transcriptRef.current?.seek(l.start_ms || 0);
                              }}
                            >
                              ▶ {formatMs(l.start_ms)}
                            </button>
                          </div>
                          <p className="mt-1 italic text-base-content/80">
                            "{l.quote_translated || l.quote}"
                          </p>
                          <p className="text-xs text-base-content/60">
                            {l.explanation}
                          </p>
                          <div className="mt-2 flex items-center gap-1.5">
                            <TriageButton
                              kind="confirm"
                              active={l.status === "confirmed"}
                              onClick={(e) => {
                                e.stopPropagation();
                                setLeadStatus(l.id, l.status === "confirmed" ? "new" : "confirmed");
                              }}
                            />
                            {l.status === "dismissed" ? (
                              <TriageButton
                                kind="restore"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setLeadStatus(l.id, "new");
                                }}
                              />
                            ) : (
                              /* DaisyUI dropdown (same as the New Case menu): opens on focus, closes when focus leaves, e.g. a click anywhere else. */
                              <div
                                className="dropdown dropdown-bottom"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <TriageButton kind="dismiss" tabIndex={0} />
                                <ul
                                  tabIndex={0}
                                  className="dropdown-content menu bg-base-100 rounded-lg z-[30] w-56 p-2 shadow border border-base-300 mt-1"
                                >
                                  {DISMISS_REASONS.map((r) => (
                                    <li key={r.value}>
                                      <button
                                        className="flex flex-col items-start gap-0 hover:bg-gray-100 dark:hover:bg-base-200"
                                        onClick={() => {
                                          setLeadStatus(
                                            l.id,
                                            "dismissed",
                                            r.value,
                                          );
                                          (
                                            document.activeElement as HTMLElement | null
                                          )?.blur();
                                        }}
                                      >
                                        <span className="text-sm text-base-content">
                                          {r.label}
                                        </span>
                                        <span className="text-[11px] text-base-content/50">
                                          {r.hint}
                                        </span>
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
              {call.glossary && call.glossary.length > 0 && (
                <section data-rail-section="slang">
                  <h3 className="font-semibold text-base-content mb-1">
                    Slang & coded language
                  </h3>
                  <AiDisclaimer kind="glossary" size="sm" className="mb-2" />
                  {/* Dictionary entries: the term reads as a headword, the gloss
                      beneath it, and confidence is a quiet meter instead of a number. */}
                  <ul className="divide-y divide-base-300 rounded-lg border border-base-300 bg-base-100">
                    {call.glossary.map((g, i) => {
                      const canSeek = g.startMs != null;
                      const pct = Math.round(g.confidence * 100);
                      return (
                        <li key={i}>
                          <button
                            type="button"
                            disabled={!canSeek}
                            onClick={() =>
                              canSeek && transcriptRef.current?.seek(g.startMs!)
                            }
                            className={cn(
                              "w-full text-left px-3 py-2 transition-colors",
                              canSeek
                                ? "hover:bg-base-200/70"
                                : "cursor-default",
                            )}
                            title={
                              canSeek ? "Play where it was said" : undefined
                            }
                          >
                            <div className="flex items-baseline gap-2">
                              <span className="font-semibold text-base-content leading-snug">
                                “{g.term}”
                              </span>
                              {canSeek && (
                                <span className="ml-auto shrink-0 inline-flex items-center gap-0.5 text-[11px] tabular-nums text-blue-600 dark:text-blue-300">
                                  <PlayIcon className="size-2.5" />{" "}
                                  {formatMs(g.startMs!)}
                                </span>
                              )}
                            </div>
                            <p className="mt-0.5 text-[13px] leading-5 text-base-content/75">
                              {g.likelyMeaning}
                            </p>
                            <div className="mt-1.5 flex items-center gap-2">
                              <Tooltip
                                placement="topLeft"
                                overlay={`${pct}% confidence in this reading`}
                              >
                                <span
                                  className="flex gap-0.5"
                                  aria-label={`${pct}% confidence`}
                                >
                                  {[0, 1, 2, 3, 4].map((n) => (
                                    <span
                                      key={n}
                                      className={cn(
                                        "h-1 w-3 rounded-full",
                                        pct >= (n + 1) * 20 - 10
                                          ? "bg-amber-400 dark:bg-amber-500"
                                          : "bg-base-300",
                                      )}
                                    />
                                  ))}
                                </span>
                              </Tooltip>
                              {g.literal && (
                                <span className="text-[11px] text-base-content/50 truncate">
                                  lit. {g.literal}
                                </span>
                              )}
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}
              {call.mentions.length > 0 && (
                <section data-rail-section="people">
                  <h3 className="font-semibold text-base-content mb-1">
                    People & things mentioned
                  </h3>
                  <AiDisclaimer kind="entities" size="sm" className="mb-2" />
                  <ul className="flex flex-wrap gap-1">
                    {[
                      ...new Map(
                        call.mentions.map((m) => [m.entity.id, m]),
                      ).values(),
                    ].map((m) => (
                      <li key={m.entity.id}>
                        <Link
                          to={`/investigations/${investigationId}/people?entity=${m.entity.id}`}
                          state={{ back: "call" }}
                          className="text-xs px-2 py-0.5 rounded-full bg-base-200 hover:bg-base-300"
                        >
                          {m.entity.canonical_name}{" "}
                          <span className="text-base-content/40">
                            {m.entity.type}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              <section data-rail-section="notes">
                <h3 className="font-semibold text-base-content mb-1">Notes</h3>
                <textarea
                  className="textarea textarea-bordered bg-base-100 w-full text-sm"
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  onBlur={() =>
                    notes !== (call.analyst_notes || "") &&
                    patch({ analyst_notes: notes })
                  }
                  placeholder="Your notes on this call"
                />
              </section>
              {call.topics && call.topics.length > 0 && (
                <section data-rail-section="topics">
                  <h3 className="font-semibold text-base-content mb-1">
                    Topics
                  </h3>
                  <AiDisclaimer kind="topics" size="sm" className="mb-2" />
                  <div className="flex flex-wrap gap-1">
                    {call.topics.map((t, i) => (
                      <span
                        key={i}
                        className="text-xs px-2 py-0.5 rounded-full bg-base-200"
                      >
                        {t.topic}
                      </span>
                    ))}
                  </div>
                </section>
              )}
            </aside>
          </div>
        </div>
      </div>
      <SlideDrawer
        open={chatOpen}
        storageKey="pnai-investigationChatWidth"
        className="border-l border-base-300 bg-base-100"
      >
        <InvestigationChat
          investigationId={investigationId}
          compact
          focusCall={{
            id: callId,
            label: `${callParties(call).primary}${call.call_started_at ? ` · ${new Date(call.call_started_at).toLocaleDateString([], { month: "short", day: "numeric" })}` : ""}`,
          }}
          onClose={() => setChatOpen(false)}
          onSeek={(cId, ms) => {
            if (cId !== callId) return false;
            transcriptRef.current?.seek(ms);
            return true;
          }}
        />
      </SlideDrawer>

      <SpeakerModal
        open={speakerEdit != null}
        label={speakerEdit || ""}
        participant={call.participants?.find((p) => p.label === speakerEdit)}
        suggestion={call.speaker_suggestions?.find(
          (s) => s.label === speakerEdit,
        )}
        propagateCount={call.same_number_unnamed}
        onClose={() => setSpeakerEdit(null)}
        onSave={(values) => saveSpeaker(speakerEdit || "", values)}
        onDismissSuggestion={(name) =>
          dismissSuggestion(speakerEdit || "", name)
        }
      />

      {call.transcript && (
        <ExportTranscriptModal
          open={exportOpen}
          onClose={() => setExportOpen(false)}
          investigationId={investigationId}
          callId={callId}
          defaultCaseId={caseNumber || investigationId}
          defaultTitle={call.file.original_file_name}
        />
      )}
    </div>
  );
};

export default CallDetail;
